import type { ComboboxItemWithValue } from "@replo/design-system/components/combobox/Combobox";
import type { ReploIntegration } from "schemas/generated/integration";
import type { IntegrationType } from "./IntegrationForm";

import * as React from "react";

import { Badge } from "@replo/design-system/components/badge/Badge";
import { Button } from "@replo/design-system/components/button/Button";
import {
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxRoot,
} from "@replo/design-system/components/combobox/Combobox";
import { Empty } from "@replo/design-system/components/empty/Empty";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { skipToken } from "@tanstack/react-query";
import { ConcentricRingLogo } from "@v2/components/ConcentricRingLogo";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { LoadedSettings } from "@v2/features/settings/SettingsCard";
import IntegrationCard from "@v2/features/settings/shop/integrations/IntegrationCard";
import { ShopifyIntegration } from "@v2/features/settings/shop/integrations/ShopifyIntegration";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { integrationRouter } from "@v2/trpc/routes/integration";
import { stripeConnectRouter } from "@v2/trpc/routes/stripeConnect";
import { routes } from "@v2/utils/router";
import { ChevronDown, Pencil, Search } from "lucide-react";
import { generatePath, useNavigate } from "react-router-dom";

import { INTEGRATION_CONFIGS } from "./config";
import { IntegrationForm } from "./IntegrationForm";

const legacyV1Integrations = [
  {
    documentationUrl: "https://help.cleansizecharts.com/article/111-replo",
    heroDescription:
      "The integration allows Replo customers the ability to add size charts anywhere on their store via Replo sections",
    image: "https://public.replocdn.com/integrations/clean-size-charts.png",
    name: "Clean Size Charts",
    slug: "clean-size-charts",
  },
  {
    documentationUrl:
      "https://support.replo.app/hc/en-us/articles/18443278985997-Fera-ai",
    heroDescription:
      "Increase sales with beautiful, customizable product reviews photo reviews & video reviews from Fera.",
    image: "https://public.replocdn.com/integrations/fera.png",
    name: "Fera",
    slug: "fera",
  },
  {
    documentationUrl:
      "https://support.replo.app/hc/en-us/articles/17512732740621-Google-Analytics",
    heroDescription:
      "Google Analytics lets you see in real time how many people are on your store – as do Shopify’s analytics reports – and where they’ve come from. This is perfect if you’ve just launched a social media or email campaign and are keeping an eye on expected spikes in traffic.",
    image: "https://public.replocdn.com/integrations/google-analytics.jpg",
    name: "Google Analytics",
    slug: "google-analytics",
  },
  {
    documentationUrl:
      "https://support.replo.app/hc/en-us/articles/19381105317133-Infinite-Options",
    heroDescription:
      "Let your shoppers select the product options for items they purchase with dropdown menus, color and variant image swatches, and radio buttons. You can even bundle add-on products to your product option selections to increase prices. ",
    image: "https://public.replocdn.com/integrations/infinite-options.jpg",
    name: "Infinite Options",
    slug: "infinite-options",
  },
  {
    documentationUrl:
      "https://support.replo.app/hc/en-us/articles/19211662346381-Intelligems",
    heroDescription:
      "Elevate your performance with Intelligems. Seamlessly A/B test anything on your store: Content Testing helps you experiment with themes, landing pages, UX and content to boost conversion and reduce CAC.",
    image: "https://public.replocdn.com/integrations/intelligems.png",
    name: "Intelligems",
    slug: "intelligems",
  },
  {
    documentationUrl:
      "https://support.replo.app/hc/en-us/articles/17512627252109-Judge-me",
    heroDescription:
      "Judge.me is a fast-loading, fully customizable review app in 38 languages. Schedule automatic emails to get reviews with photos and videos after order fulfillment or delivery, or import them from other apps.",
    image: "https://public.replocdn.com/integrations/judge-me.png",
    name: "Judge.me",
    slug: "judge-me",
  },
  {
    documentationUrl: "https://support.replo.app/articles/5052156457-klaviyo",
    heroDescription:
      "Klaviyo helps over 100,000 paying customers centralize their data from 300+ integrations and make it instantly usable in targeted, personalized, and automated marketing across email, SMS, forms, mobile push, and reviews, all from one log-in.",
    image: "https://public.replocdn.com/integrations/klaviyo.jpg",
    name: "Klaviyo",
    slug: "klaviyo",
  },
  {
    documentationUrl:
      "https://support.replo.app/hc/en-us/articles/18418422076813-KnoCommerce",
    heroDescription:
      "We're KnoCommerce, the post-purchase survey (PPS) platform that delivers valuable insights through customer surveys. Uncover your customers' motivations and preferences, and scale your marketing channels through effective attribution. ",
    image: "https://public.replocdn.com/integrations/knocommerce.jpg",
    name: "KnoCommerce",
    slug: "knocommerce",
  },
  {
    documentationUrl:
      "https://support.replo.app/hc/en-us/categories/17095390479245-Integrations",
    heroDescription:
      "Create Shopify landing pages and sections with Okendo product reviews.",
    image: "https://public.replocdn.com/integrations/okendo.webp",
    name: "Okendo",
    slug: "okendo",
  },
  {
    documentationUrl:
      "https://support.replo.app/hc/en-us/articles/17513084332685-Postscript",
    heroDescription:
      "With Postscript, drive conversations and conversions across all stages of the customer journey. Whether 1:1 or at scale, Postscript assures your texts are customized, conversational, and compliant. ",
    image: "https://public.replocdn.com/integrations/postscript.webp",
    name: "Postscript",
    slug: "postscript",
  },
  {
    documentationUrl:
      "https://support.replo.app/hc/en-us/articles/17513367706765-Rebuy",
    heroDescription:
      "Rebuy unlocks the power of personalization by combining proprietary AI/ML technology with modern (no-code, low-code, and custom code) software, industry-leading integrations, fully-customizable merchandising rules and A/B/n testing.",
    image: "https://public.replocdn.com/integrations/rebuy.jpg",
    name: "Rebuy",
    slug: "rebuy",
  },
  {
    documentationUrl: "https://support.replo.app/articles/1713035642-recharge",
    heroDescription:
      "Create landing pages and Shopify sections with add to cart buttons integrated directly with Recharge.",
    image: "https://public.replocdn.com/integrations/recharge.webp",
    name: "Recharge",
    slug: "recharge",
  },
  {
    documentationUrl:
      "https://support.replo.app/hc/en-us/articles/18441300446477-Shopify-Product-Reviews",
    heroDescription:
      "You can use the Product Reviews app to add a customer review feature to your products. This lets your customers engage with your business, as well as each other, to encourage sales.",
    image:
      "https://public.replocdn.com/integrations/shopify-product-reviews.png",
    name: "Shopify Product Reviews",
    slug: "shopify-product-reviews",
  },
  {
    documentationUrl:
      "https://support.replo.app/hc/en-us/articles/17130500372877-Skio",
    heroDescription:
      "Skio helps brands sell subscriptions without ripping their hair out.",
    image: "https://public.replocdn.com/integrations/skio.png",
    name: "Skio",
    slug: "skio",
  },
  {
    documentationUrl:
      "https://support.replo.app/hc/en-us/articles/18440847987597-Stamped",
    heroDescription:
      "Stamped helps you capture and showcase high-converting reviews & ratings, photos & videos, and Q&A.",
    image: "https://public.replocdn.com/integrations/stamped-io.jpg",
    name: "Stamped.io",
    slug: "stamped-io",
  },
  {
    documentationUrl:
      "https://support.replo.app/hc/en-us/articles/17512694105869-TikTok",
    heroDescription:
      "Find and engage new customers with TikTok Ads and TikTok Shop",
    image: "https://public.replocdn.com/integrations/tiktok.png",
    name: "Tiktok",
    slug: "tiktok",
  },
  {
    documentationUrl:
      "https://support.replo.app/hc/en-us/articles/17512762636301-Triple-Whale",
    heroDescription:
      "Triple Whale brings the metrics & attribution that matter most into one easy-to-use dashboard.",
    image: "https://public.replocdn.com/integrations/triplewhale.png",
    name: "Triplewhale",
    slug: "triplewhale",
  },
  {
    documentationUrl:
      "https://support.replo.app/hc/en-us/articles/17512680109069-Vimeo",
    heroDescription:
      "Make impressive, powerful, sale-making videos in minutes!",
    image: "https://public.replocdn.com/integrations/vimeo.png",
    name: "Vimeo",
    slug: "vimeo",
  },
  {
    documentationUrl:
      "https://support.replo.app/hc/en-us/articles/17512705403277-YouTube",
    heroDescription:
      "Tag products to your videos to make it easy for followers to discover, shop, and buy.",
    image: "https://public.replocdn.com/integrations/youtube.png",
    name: "Youtube",
    slug: "youtube",
  },
];

const INTEGRATIONS_COMBOBOX_ITEMS: ComboboxItemWithValue[] = Object.entries(
  INTEGRATION_CONFIGS,
).map(([integrationType, { title, description, image }]) => ({
  value: integrationType,
  label: title,
  description,
  size: "lg",
  startEnhancer: (
    <Badge size="xl" className="border-none shadow-none">
      <img src={image} alt={title} style={{ width: "32px", height: "32px" }} />
    </Badge>
  ),
}));

const IntegrationsCombobox: React.FC<{
  integration: IntegrationType | null;
  onIntegrationChange: (value: IntegrationType) => void;
}> = ({ integration, onIntegrationChange }) => {
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const selectedItem =
    INTEGRATIONS_COMBOBOX_ITEMS.find((item) => item.value === integration) ??
    null;

  return (
    <ComboboxRoot
      items={INTEGRATIONS_COMBOBOX_ITEMS}
      value={selectedItem}
      onValueChange={(item) => {
        if (item) {
          onIntegrationChange(item.value as IntegrationType);
        }
      }}
      itemToStringValue={(item) => String(item.label)}
      isItemEqualToValue={(a, b) => a.value === b.value}
    >
      <ComboboxInput
        placeholder="Search"
        startEnhancer={<Search className="size-3" />}
        endEnhancer={<ChevronDown className="size-3" />}
        size="base"
        wrapperRef={anchorRef}
      />
      <ComboboxContent anchor={anchorRef}>
        <ComboboxEmpty />
        <ComboboxList>
          {(item: ComboboxItemWithValue) => (
            <ComboboxItem key={item.value} {...item} value={item} />
          )}
        </ComboboxList>
      </ComboboxContent>
    </ComboboxRoot>
  );
};

const AddIntegrationSection: React.FC = () => {
  const [integration, setIntegration] = React.useState<IntegrationType | null>(
    null,
  );

  // NOTE (Max, 2025-07-31): If an integration is selected, we show the form that allows the user
  // to enter the integration config details. Otherwise, we show the combobox that allows them
  // to select an integration.
  return (
    <>
      {integration ? (
        <IntegrationForm
          integration={integration}
          handleCancel={() => setIntegration(null)}
        />
      ) : (
        <IntegrationsCombobox
          integration={integration}
          onIntegrationChange={setIntegration}
        />
      )}
    </>
  );
};

const IntegrationsHome: React.FC = () => {
  const { project } = useCurrentProjectContext();
  const isReploHosted = project?.hostingType === "replo";

  const sections = [
    {
      title: "Available Integrations",
      description:
        "Helpful guides to get you up and running with your favorite integrations.",
      component: (
        <div className="flex flex-col gap-4 w-full overflow-hidden">
          <ShopifyIntegrationComponent />

          <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 min-w-0 w-full">
            {legacyV1Integrations.map((integration) => (
              <IntegrationCard
                key={integration.name}
                title={integration.name}
                image={integration.image}
                redirectUrl={integration.documentationUrl}
              />
            ))}
          </div>
        </div>
      ),
    },
  ];

  const sectionsV2 = [
    {
      title: "Add an Integration",
      description: "Find your integration and plug in the details to connect.",
      component: <AddIntegrationSection />,
    },
    {
      title: "Your Integrations",
      component: <IntegrationsListSection />,
    },
  ];

  return <LoadedSettings sections={isReploHosted ? sectionsV2 : sections} />;
};

const ShopifyIntegrationComponent: React.FC = () => {
  const { project } = useCurrentProjectContext();
  const projectId = useCurrentProjectId();

  // Get Stripe Connect onboarding status
  const { data: onboardingStatus } =
    stripeConnectRouter.stripeConnectGetOnboardingStatus.useQuery(
      projectId ? { projectId } : skipToken,
    );

  if (!project) {
    return <Skeleton className="h-[190px] w-full" />;
  }

  // Only show Shopify integration if Stripe Connect is not fully onboarded
  const isStripeFullyOnboarded =
    onboardingStatus?.isConnected && onboardingStatus?.onboardingCompleted;

  if (isStripeFullyOnboarded) {
    return null;
  }

  return (
    <div className="border-0.5 border-border rounded-xl p-4">
      <ShopifyIntegration project={project} />
    </div>
  );
};

const IntegrationsListSection: React.FC = () => {
  const projectId = useCurrentProjectId();

  const { data: integrations, isLoading: isLoadingIntegrations } =
    integrationRouter.find.useQuery(
      projectId
        ? {
            projectId,
          }
        : skipToken,
    );

  // Check if Shopify integration card is showing
  const { data: onboardingStatus } =
    stripeConnectRouter.stripeConnectGetOnboardingStatus.useQuery(
      projectId ? { projectId } : skipToken,
    );

  const isStripeFullyOnboarded =
    onboardingStatus?.isConnected && onboardingStatus?.onboardingCompleted;
  const isShopifyCardShowing = !isStripeFullyOnboarded;

  return (
    <div className="space-y-4 w-full overflow-hidden">
      <ShopifyIntegrationComponent />
      {isLoadingIntegrations ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 min-w-0 w-full">
          <Skeleton className="h-[143px] w-full" />
          <Skeleton className="h-[143px] w-full" />
          <Skeleton className="h-[143px] w-full" />
          <Skeleton className="h-[143px] w-full" />
        </div>
      ) : (
        <LoadedIntegrations
          integrations={integrations ?? []}
          projectId={projectId ?? ""}
          isShopifyCardShowing={isShopifyCardShowing}
        />
      )}
    </div>
  );
};

const LoadedIntegrations: React.FC<{
  integrations: ReploIntegration[];
  projectId: string;
  isShopifyCardShowing: boolean;
}> = ({ integrations, projectId, isShopifyCardShowing }) => {
  const navigate = useNavigate();

  const nonShopifyIntegrations = integrations.filter(
    // NOTE (Max, 2025-07-31): We show the Shopify Integration separately,
    // so we don't need to show it here again.
    // NOTE: We also show StripeConnect separately, so exclude it too.
    (integration) => {
      return (
        integration.type !== "ShopifyIntegrationConfig" &&
        integration.type !== "StripeConnect"
      );
    },
  );

  // Don't show empty state if Shopify card is showing above (redundant)
  if (nonShopifyIntegrations.length == 0 && !isShopifyCardShowing) {
    return (
      <div className="flex justify-center items-center py-8">
        <Empty
          media={
            <ConcentricRingLogo
              variant="replo"
              isSelected={false}
              className="w-56 h-56"
            />
          }
          title="No other integrations found"
          description="Search and add another integration above."
          size="sm"
        />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 min-w-0 w-full">
      {nonShopifyIntegrations?.map((integration) => {
        const { title, description, image } =
          INTEGRATION_CONFIGS[integration.type];

        return (
          <div
            key={integration.id}
            className="p-4 border-0.5 border-border rounded-lg hover:bg-button-secondary-hover cursor-pointer min-h-[120px] w-full min-w-0 max-w-full"
            onClick={() => {
              navigate(
                generatePath(routes.shops.settings.shops.integrations.edit, {
                  projectId,
                  integrationId: integration.id,
                }),
              );
            }}
          >
            <div className="flex flex-col gap-2">
              <div className="flex flex-row justify-between">
                <img
                  src={image}
                  alt={title}
                  style={{
                    width: "32px",
                    height: "32px",
                  }}
                  className="rounded-lg"
                />
                <Button
                  type="button"
                  icon
                  size="sm"
                  variant="secondary"
                  onClick={undefined}
                >
                  <Pencil />
                </Button>
              </div>

              <div className="flex flex-col gap-0.5">
                <span className="header-h6 break-words">{title}</span>
                <span className="text-xs text-muted break-words leading-relaxed">
                  {description}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default IntegrationsHome;
